import { Injectable } from '@angular/core';
import { BuilderComponentType, BuilderNode, ValidationResult, ValidationErrorType } from '../interfaces/builder-screen.models';

/**
 * Serviço de validação de hierarquia do Builder
 * Garante que componentes sejam adicionados apenas em locais válidos
 * Estrutura obrigatória: Tab → Panel → Componentes
 */
@Injectable()
export class BuilderValidationService {

  /**
   * Valida se um componente pode ser adicionado na posição atual
   * @param type Tipo do componente a ser adicionado
   * @param selectedNode Nó atualmente selecionado (onde será adicionado)
   * @param allNodes Todos os nós do canvas
   * @returns ValidationResult com status e mensagens
   */
  validateComponentPlacement(
    type: BuilderComponentType,
    selectedNode: BuilderNode | null,
    allNodes: ReadonlyArray<BuilderNode>
  ): ValidationResult {
    // 1. Se está adicionando Tabs ou Panel, sempre permitir
    // (Tabs pode ser raiz, Panel pode ser filho de Tabs)
    if (type === 'tabs') {
      // Tabs não podem ser aninhadas dentro de outros componentes
      if (selectedNode && selectedNode.type !== 'tabs') {
        return {
          isValid: false,
          errorType: ValidationErrorType.INVALID_CONTAINER_NESTING,
          errorMessage: 'Tabs não podem ser aninhadas dentro de outros componentes',
          suggestedAction: 'Desselecione qualquer componente e adicione as Tabs na raiz do canvas'
        };
      }
      return { isValid: true };
    }

    if (type === 'panel') {
      // Panel deve estar dentro de Tabs
      if (!selectedNode || selectedNode.type !== 'tabs') {
        return {
          isValid: false,
          errorType: ValidationErrorType.INVALID_CONTAINER_NESTING,
          errorMessage: 'Panels devem ser criados dentro de Tabs',
          suggestedAction: 'Selecione um componente de Tabs primeiro'
        };
      }
      return { isValid: true };
    }

    // 2. Para outros componentes (text, textarea, number, etc.), DEVEM estar dentro de Panel ou Row
    // Verificar se não há seleção (canvas vazio ou nada selecionado)
    if (!selectedNode) {
      return {
        isValid: false,
        errorType: ValidationErrorType.NO_PANEL_PARENT,
        errorMessage: 'Componentes devem ser adicionados dentro de um Panel',
        suggestedAction: 'Selecione um Panel primeiro. Se o canvas estiver vazio, uma estrutura base será criada automaticamente.'
      };
    }

    // 3. Verificar se o parent é válido
    const canAdd = this.canAddToParent(type, selectedNode.type);
    
    if (!canAdd) {
      // Se selecionou Tabs, precisa selecionar um Panel
      if (selectedNode.type === 'tabs') {
        return {
          isValid: false,
          errorType: ValidationErrorType.NO_PANEL_PARENT,
          errorMessage: 'Componentes não podem ser adicionados diretamente em Tabs',
          suggestedAction: 'Selecione um Panel dentro da Tab, ou crie um novo Panel'
        };
      }

      // Caso genérico de parent inválido
      return {
        isValid: false,
        errorType: ValidationErrorType.INVALID_CONTAINER_NESTING,
        errorMessage: `Componentes do tipo "${type}" não podem ser adicionados dentro de "${selectedNode.type}"`,
        suggestedAction: 'Selecione um Panel ou Row válido'
      };
    }

    return { isValid: true };
  }

  /**
   * Verifica se um tipo de componente pode ser adicionado a um tipo de parent
   * Matriz de compatibilidade entre tipos
   */
  canAddToParent(componentType: BuilderComponentType, parentType: BuilderComponentType): boolean {
    // Tabs pode conter: Panel
    if (parentType === 'tabs') {
      return componentType === 'panel';
    }

    // Panel pode conter: Row e todos os componentes (exceto Tabs e Panel)
    if (parentType === 'panel') {
      // Não pode conter containers de alto nível
      if (componentType === 'tabs' || componentType === 'panel') {
        return false;
      }
      // Pode conter Row e todos os demais componentes
      return true;
    }

    // Row pode conter: todos os componentes (exceto Tabs, Panel e Row)
    if (parentType === 'row') {
      // Não pode conter containers
      if (componentType === 'tabs' || componentType === 'panel' || componentType === 'row') {
        return false;
      }
      // Pode conter todos os campos/componentes
      return true;
    }

    // Outros tipos não podem ser parents
    return false;
  }

  /**
   * Busca o Panel mais próximo na hierarquia
   * Usado para sugestões ao usuário
   */
  findNearestValidPanel(selectedNode: BuilderNode | null, allNodes: ReadonlyArray<BuilderNode>): BuilderNode | null {
    if (!selectedNode) {
      // Buscar o primeiro panel disponível
      return allNodes.find(n => n.type === 'panel') || null;
    }

    // Se o selecionado já é um panel, retornar ele mesmo
    if (selectedNode.type === 'panel') {
      return selectedNode;
    }

    // Se está dentro de um panel (parentId é um panel), retornar o parent
    if (selectedNode.parentId) {
      const parent = allNodes.find(n => n.id === selectedNode.parentId);
      if (parent && parent.type === 'panel') {
        return parent;
      }
    }

    // Buscar qualquer panel disponível
    return allNodes.find(n => n.type === 'panel') || null;
  }

  /**
   * Verifica se existe pelo menos um Tab + Panel no canvas
   * Estrutura base mínima necessária
   */
  hasBaseStructure(nodes: ReadonlyArray<BuilderNode>): boolean {
    const hasTabs = nodes.some(n => n.type === 'tabs');
    const hasPanel = nodes.some(n => n.type === 'panel');
    return hasTabs && hasPanel;
  }

  /**
   * Verifica se o canvas está completamente vazio
   */
  isCanvasEmpty(nodes: ReadonlyArray<BuilderNode>): boolean {
    return nodes.length === 0;
  }

  /**
   * Retorna uma mensagem de erro formatada para exibição
   */
  getFormattedErrorMessage(validation: ValidationResult): string {
    if (validation.isValid) {
      return '';
    }

    let message = `❌ ${validation.errorMessage}`;
    if (validation.suggestedAction) {
      message += `\n\n💡 ${validation.suggestedAction}`;
    }
    return message;
  }

  /**
   * Valida toda a estrutura do canvas
   * Retorna lista de problemas encontrados (para migration/debugging)
   */
  validateCanvasStructure(nodes: ReadonlyArray<BuilderNode>): Array<{ nodeId: string; issue: string }> {
    const issues: Array<{ nodeId: string; issue: string }> = [];

    for (const node of nodes) {
      // Verificar se componentes estão fora de panels
      if (node.type !== 'tabs' && node.type !== 'panel') {
        // Deve ter um parentId
        if (!node.parentId) {
          issues.push({
            nodeId: node.id,
            issue: `Componente "${node.type}" está fora de um Panel (sem parentId)`
          });
          continue;
        }

        // O parent deve ser um panel ou row
        const parent = nodes.find(n => n.id === node.parentId);
        if (parent && parent.type !== 'panel' && parent.type !== 'row') {
          issues.push({
            nodeId: node.id,
            issue: `Componente "${node.type}" tem parent inválido: "${parent.type}"`
          });
        }
      }

      // Verificar se panels estão dentro de tabs
      if (node.type === 'panel') {
        if (!node.parentId) {
          issues.push({
            nodeId: node.id,
            issue: 'Panel está fora de Tabs (sem parentId)'
          });
        } else {
          const parent = nodes.find(n => n.id === node.parentId);
          if (parent && parent.type !== 'tabs') {
            issues.push({
              nodeId: node.id,
              issue: `Panel tem parent inválido: "${parent.type}" (deveria ser "tabs")`
            });
          }
        }
      }
    }

    return issues;
  }
}

